/**
 * ID + Social Security Card Converter (Feb 2026 user request).
 *
 * Bureau / lender portals typically reject IDs > ~150 KB and reject PDFs
 * outright.  This tool converts either or both of a client's ID and Social
 * Security card into portal-ready JPEG(s).
 *
 *   • Output: JPEG only, fitted to 640×640 at 500 DPI, auto-contrast +
 *     unsharp mask, quality auto-backs off to keep the file portal-safe.
 *   • Modes:
 *       – ID only              → single JPEG auto-downloads
 *       – ID + SSN "separate"  → two JPEGs auto-download back-to-back
 *       – ID + SSN "combined"  → one JPEG (side-by-side OR vertical)
 *         with a MEDIUM gap so the two cards never touch.
 *
 * Backend endpoints: POST /api/id-converter/convert (single file)
 *                    POST /api/id-converter/combine (both files, layout param)
 */
import React, { useCallback, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  IdCard, Upload, Download, X, Image as ImageIcon,
  CheckCircle2, Loader2, RefreshCcw, Layers, Rows3, Columns2, ArrowLeft,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.bmp,.tiff";
const MAX_BYTES = 25 * 1024 * 1024;

const formatBytes = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

// Trigger a browser download from a Blob URL.  Kept inline so the
// convert / combine handlers stay side-effect-free at the top.
const triggerDownload = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// Small reusable drop-zone tile.  Used for both the ID and SSN slots.
function DropSlot({ label, file, onPick, testid, accent = "cyan", optional = false }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const accentClass = accent === "amber"
    ? "text-amber-300 hover:border-amber-400/40 hover:bg-white/[0.04]"
    : "text-cyan-300 hover:border-cyan-400/40 hover:bg-white/[0.04]";
  const dragClass = accent === "amber"
    ? "border-amber-400 bg-amber-400/5"
    : "border-cyan-400 bg-cyan-400/5";

  return (
    <div
      data-testid={testid}
      onDrop={(e) => { e.preventDefault(); setDragActive(false); onPick(e.dataTransfer.files?.[0]); }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
        dragActive ? dragClass : `border-white/10 bg-white/[0.02] ${accentClass}`
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => onPick(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div className="flex flex-col items-center gap-2">
        <Upload className={`w-9 h-9 ${accent === "amber" ? "text-amber-300/70" : "text-cyan-300/70"}`} />
        <div className="text-sm font-semibold text-white">
          {label}{" "}
          {optional && <span className="text-xs text-slate-400 font-normal">(optional)</span>}
        </div>
        {file ? (
          <div className="text-[11px] text-slate-300 truncate max-w-full">
            {file.name} · {formatBytes(file.size)}
          </div>
        ) : (
          <div className="text-[11px] text-slate-500">
            PDF · HEIC · JPG · PNG · WebP · BMP · TIFF — up to 25&nbsp;MB
          </div>
        )}
      </div>
    </div>
  );
}

export default function IDConverterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Preserve the FTC-builder (or any caller) context so ← Back returns
  // the user exactly where they came from, without dropping session state.
  const returnTo = searchParams.get("returnTo") || "";
  const [idFile, setIdFile] = useState(null);
  const [ssnFile, setSsnFile] = useState(null);
  // "separate" — auto-downloads two JPEGs back-to-back.
  // "side_by_side" / "vertical" — merges both onto one JPEG page.
  const [combineLayout, setCombineLayout] = useState("side_by_side");
  const [outputMode, setOutputMode] = useState("separate"); // separate | combined
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]); // [{ url, name, kind, width, height, dpi, quality, bytes }]

  const pick = (which) => (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("File exceeds 25 MB.  Please choose a smaller image or PDF.");
      return;
    }
    if (which === "id") setIdFile(f);
    else setSsnFile(f);
    setResults([]);
  };

  const clearAll = () => {
    results.forEach((r) => URL.revokeObjectURL(r.url));
    setIdFile(null);
    setSsnFile(null);
    setResults([]);
  };

  const parseResult = (headers, blob, kind, fallbackName) => {
    const url = URL.createObjectURL(blob);
    const cd = headers?.["content-disposition"] || "";
    const m = cd.match(/filename="([^"]+)"/);
    return {
      url,
      blob,
      kind,
      name: m?.[1] || fallbackName,
      width: Number(headers?.["x-width"]) || null,
      height: Number(headers?.["x-height"]) || null,
      dpi: Number(headers?.["x-dpi"]) || null,
      quality: Number(headers?.["x-final-quality"]) || null,
      layout: headers?.["x-layout"] || null,
      bytes: blob.size,
    };
  };

  // Poll the /status endpoint until the background job finishes.  Uses
  // an exponential-ish back-off: fast at first (job may already be done),
  // then slower once the LLM/PIL pipeline is confirmed running.
  const pollJob = async (jobId, onProgress) => {
    const started = Date.now();
    const HARD_TIMEOUT_MS = 5 * 60 * 1000; // 5 min cap — matches worst-case PIL time.
    let attempt = 0;
    while (true) {
      if (Date.now() - started > HARD_TIMEOUT_MS) {
        throw new Error("Conversion took too long — please retry with a smaller file.");
      }
      // Poll intervals: 500, 500, 1000, 1500, 2000, then cap at 2000 ms.
      const delay = attempt < 2 ? 500 : Math.min(2000, 500 + attempt * 500);
      await new Promise((r) => setTimeout(r, delay));
      attempt += 1;
      const { data } = await axios.get(
        `${API}/api/id-converter/status/${jobId}`,
        { timeout: 15000 },
      );
      if (data?.status === "done") return data;
      if (data?.status === "error") {
        throw new Error(data.error || "Conversion failed.");
      }
      if (typeof onProgress === "function") {
        onProgress(data?.stage || data?.progress || "Working…");
      }
    }
  };

  // Fetch the completed JPEG bytes for a done job and construct a result
  // record identical to the old sync response shape.
  const fetchJobResult = async (jobId, kind, fallbackName) => {
    const res = await axios.get(
      `${API}/api/id-converter/result/${jobId}`,
      { responseType: "blob", timeout: 90000 },
    );
    const blob = new Blob([res.data], { type: "image/jpeg" });
    return parseResult(res.headers, blob, kind, fallbackName);
  };

  const [progressMsg, setProgressMsg] = useState("");

  const doConvert = async () => {
    if (!idFile) {
      toast.error("Upload at least an ID.");
      return;
    }
    setBusy(true);
    setProgressMsg("Uploading…");
    // Clean up any previous run before starting a fresh one.
    results.forEach((r) => URL.revokeObjectURL(r.url));
    setResults([]);

    try {
      // COMBINED — one merged JPEG (only if BOTH files are present).
      if (outputMode === "combined" && ssnFile) {
        const fd = new FormData();
        fd.append("id_file", idFile);
        fd.append("ssn_file", ssnFile);
        fd.append("layout", combineLayout);
        const startRes = await axios.post(
          `${API}/api/id-converter/combine/start`, fd,
          { timeout: 60000 },
        );
        const jobId = startRes.data?.jobId;
        if (!jobId) throw new Error("Failed to start conversion job.");
        setProgressMsg(startRes.data?.stage || "Queued…");
        await pollJob(jobId, setProgressMsg);
        const r = await fetchJobResult(jobId, "combined", "id_and_ssn.jpg");
        setResults([r]);
        triggerDownload(r.url, r.name);
        toast.success(`Downloaded ${r.name}`);
        return;
      }

      // SEPARATE — convert each file individually via the async pipeline.
      // Kick off both jobs first, then poll them in parallel, then fetch
      // both results.  This is strictly faster than sequential polling.
      const startCall = async (file, kind, fallbackName) => {
        const fd = new FormData();
        fd.append("file", file);
        const startRes = await axios.post(
          `${API}/api/id-converter/start`, fd,
          { timeout: 60000 },
        );
        const jobId = startRes.data?.jobId;
        if (!jobId) throw new Error("Failed to start conversion job.");
        return { jobId, kind, fallbackName };
      };
      const jobs = [];
      jobs.push(await startCall(
        idFile, "id",
        `${idFile.name.replace(/\.[^.]+$/, "")}_portal.jpg`,
      ));
      if (ssnFile) {
        jobs.push(await startCall(
          ssnFile, "ssn",
          `${ssnFile.name.replace(/\.[^.]+$/, "")}_portal.jpg`,
        ));
      }
      setProgressMsg(`Processing ${jobs.length} file${jobs.length > 1 ? 's' : ''}…`);
      // Poll both jobs in parallel — Promise.all resolves when both are done.
      await Promise.all(
        jobs.map((j) => pollJob(j.jobId, (m) => setProgressMsg(m))),
      );
      const out = await Promise.all(
        jobs.map((j) => fetchJobResult(j.jobId, j.kind, j.fallbackName)),
      );
      setResults(out);
      for (const r of out) {
        triggerDownload(r.url, r.name);
        await new Promise((res) => setTimeout(res, 250));
      }
      toast.success(
        out.length === 1
          ? `Downloaded ${out[0].name}`
          : `Downloaded ${out.length} portal JPEGs`,
      );
    } catch (e) {
      // Peel the JSON detail out of the blob response for a real error msg.
      let msg = e?.message || "Conversion failed.";
      const b = e?.response?.data;
      if (b && b.text) {
        try {
          const j = JSON.parse(await b.text());
          msg = j?.detail || msg;
        } catch { /* keep original */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setBusy(false);
      setProgressMsg("");
    }
  };

  // "Combined" is only meaningful when BOTH files are queued up.  If the
  // SSN is missing we visually mark the option as disabled but still let
  // the user click it — the convert handler falls back to separate.
  const canCombine = !!idFile && !!ssnFile;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <header className="mb-6">
          {/* ← Back button — always visible; when we have a returnTo,
              take the user back to the FTC builder (or wherever they
              came from) so they don't lose their in-progress flow.
              Otherwise browser-history navigate(-1). */}
          <button
            type="button"
            onClick={() => (returnTo ? navigate(returnTo) : navigate(-1))}
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 border border-cyan-400/30 hover:border-cyan-300/60 rounded-full px-3 py-1.5 mb-4 transition"
            data-testid="id-converter-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          <h1 className="flex items-center gap-3 text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-br from-teal-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
            <IdCard className="w-9 h-9 text-cyan-300" />
            ID + SSN Card Converter
          </h1>
          <p className="mt-2 text-sm text-slate-400 max-w-2xl">
            Convert a client&apos;s <span className="text-cyan-300 font-semibold">ID</span> and,
            optionally, their <span className="text-amber-300 font-semibold">Social Security Card</span>{" "}
            into portal-ready JPEG(s).  Output is fitted to{" "}
            <span className="text-cyan-300 font-semibold">6.67&nbsp;×&nbsp;8.89&nbsp;in</span> at{" "}
            <span className="text-cyan-300 font-semibold">500 DPI</span> with denoise + auto-contrast +
            aggressive sharpen — blurry phone shots become crystal-clear without altering the document.
          </p>
        </header>

        {/* Upload slots */}
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <DropSlot
            label="Government ID"
            file={idFile}
            onPick={pick("id")}
            testid="id-converter-id-dropzone"
            accent="cyan"
          />
          <DropSlot
            label="Social Security Card"
            file={ssnFile}
            onPick={pick("ssn")}
            testid="id-converter-ssn-dropzone"
            accent="amber"
            optional
          />
        </div>

        {/* Output-mode selector — only meaningful when SSN is present. */}
        {idFile && (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-4 mb-5">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3 flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" /> Output Mode
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={() => setOutputMode("separate")}
                data-testid="id-converter-mode-separate"
                className={`text-left px-4 py-3 rounded-xl border transition-all ${
                  outputMode === "separate"
                    ? "bg-cyan-500/15 border-cyan-400/70 ring-1 ring-cyan-400/40"
                    : "bg-black/30 border-white/10 hover:border-cyan-400/40"
                }`}
              >
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  <Download className="w-4 h-4 text-cyan-300" />
                  Separate downloads
                </div>
                <div className="text-[11px] text-slate-400 mt-1 leading-snug">
                  {ssnFile
                    ? "Two JPEGs auto-download back-to-back."
                    : "Just the ID converts and auto-downloads."}
                </div>
              </button>
              <button
                onClick={() => setOutputMode("combined")}
                disabled={!canCombine}
                data-testid="id-converter-mode-combined"
                className={`text-left px-4 py-3 rounded-xl border transition-all ${
                  !canCombine
                    ? "bg-black/20 border-white/5 opacity-40 cursor-not-allowed"
                    : outputMode === "combined"
                      ? "bg-cyan-500/15 border-cyan-400/70 ring-1 ring-cyan-400/40"
                      : "bg-black/30 border-white/10 hover:border-cyan-400/40"
                }`}
              >
                <div className="text-sm font-semibold text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-cyan-300" />
                  Combined on one page
                </div>
                <div className="text-[11px] text-slate-400 mt-1 leading-snug">
                  {canCombine
                    ? "Both cards on a single JPEG with a medium gap — they never touch."
                    : "Upload the Social Security card to unlock."}
                </div>
              </button>
            </div>

            {/* Layout sub-selector — only surfaces in combined mode. */}
            {outputMode === "combined" && canCombine && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                  Layout
                </div>
                <div className="inline-flex bg-black/40 border border-white/10 rounded-xl p-1">
                  <button
                    onClick={() => setCombineLayout("side_by_side")}
                    data-testid="id-converter-layout-side"
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors ${
                      combineLayout === "side_by_side"
                        ? "bg-cyan-500 text-black"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Columns2 className="w-3.5 h-3.5" /> Side-by-side
                  </button>
                  <button
                    onClick={() => setCombineLayout("vertical")}
                    data-testid="id-converter-layout-vertical"
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors ${
                      combineLayout === "vertical"
                        ? "bg-cyan-500 text-black"
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Rows3 className="w-3.5 h-3.5" /> Vertical stack
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action row */}
        {(idFile || ssnFile) && (
          <div className="flex flex-wrap gap-3 mb-5">
            <button
              onClick={doConvert}
              disabled={busy || !idFile}
              data-testid="id-converter-convert-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-br from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-xl text-sm shadow-lg shadow-cyan-900/40 transition-colors"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {progressMsg || "Converting…"}</>
              ) : (
                <><RefreshCcw className="w-4 h-4" /> Convert &amp; Download</>
              )}
            </button>
            <button
              onClick={clearAll}
              disabled={busy}
              data-testid="id-converter-clear-btn"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-white/[0.06] transition-colors"
            >
              <X className="w-4 h-4" /> Clear
            </button>
          </div>
        )}

        {/* Result panel */}
        {results.length > 0 && (
          <div
            className="rounded-2xl bg-black/40 border border-cyan-400/30 p-5"
            data-testid="id-converter-result"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <div className="font-semibold text-emerald-200">
                {results.length === 1
                  ? "Conversion complete — the portal-ready JPEG has been downloaded."
                  : `Conversion complete — both portal-ready JPEGs downloaded.`}
              </div>
            </div>
            <div className={`grid ${results.length === 1 ? "md:grid-cols-1" : "md:grid-cols-2"} gap-4`}>
              {results.map((r, i) => (
                <div
                  key={i}
                  className="bg-white/[0.02] border border-white/10 rounded-xl p-3"
                  data-testid={`id-converter-result-${r.kind}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
                    {r.kind === "id"
                      ? "Government ID"
                      : r.kind === "ssn"
                        ? "Social Security Card"
                        : `Combined (${(r.layout || combineLayout).replace(/_/g, " ")})`}
                  </div>
                  <div className="bg-black/30 rounded-lg flex items-center justify-center min-h-[180px] mb-3 p-2">
                    <img
                      src={r.url}
                      alt={`Preview ${r.kind}`}
                      className="max-w-full max-h-[360px] rounded shadow-2xl"
                      data-testid={`id-converter-preview-${r.kind}`}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-300">
                    <div className="text-slate-500 uppercase tracking-wider">Dimensions</div>
                    <div className="text-slate-100 font-medium">{r.width}×{r.height}px</div>
                    <div className="text-slate-500 uppercase tracking-wider">Resolution</div>
                    <div className="text-slate-100 font-medium">{r.dpi} DPI</div>
                    <div className="text-slate-500 uppercase tracking-wider">JPEG Quality</div>
                    <div className="text-slate-100 font-medium">{r.quality}</div>
                    <div className="text-slate-500 uppercase tracking-wider">File Size</div>
                    <div className="text-emerald-300 font-semibold">{formatBytes(r.bytes)}</div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => triggerDownload(r.url, r.name)}
                      className="inline-flex items-center gap-1.5 bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow shadow-emerald-900/30 transition-colors"
                      data-testid={`id-converter-download-${r.kind}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Download Again
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/5">
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] text-slate-200 text-xs px-3 py-2 rounded-lg transition-colors"
                data-testid="id-converter-new-btn"
              >
                <ImageIcon className="w-4 h-4" /> Convert Another Client
              </button>
            </div>
          </div>
        )}

        {/* Help footer */}
        <div className="mt-8 text-xs text-slate-500 border-t border-white/5 pt-4 space-y-1">
          <div><strong className="text-slate-300">Tip:</strong> If a portal still rejects the file, try clearing your browser cache or renaming to <code className="text-cyan-300">.jpg</code>.</div>
          <div>Combined output uses a 200&nbsp;px gap (~0.4&nbsp;in @ 500 DPI) so the ID and SSN card never touch.  All output uses 4:2:2 chroma subsampling and progressive JPEG at quality 95 for crystal-clear text.</div>
        </div>
      </div>
    </div>
  );
}
